import React, { useState, useEffect, useMemo } from "react";
import api from "../services/api";
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Stack,
  Button,
  Loader,
  Alert,
  TextInput,
  Select,
  Table,
  ScrollArea,
  Divider,
  Pagination,
  Modal,
  PasswordInput,
  Textarea,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconAlertCircle, IconDownload, IconLock } from "@tabler/icons-react";
import { PROVIDERS } from "../constants/providers";

const normalizeDateOnly = (value) => {
  if (!value) return "";

  if (typeof value === "string") {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }

    const isoMatch = value.match(/^(\d{4}-\d{2}-\d{2})T/);
    if (isoMatch) {
      return isoMatch[1];
    }
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  return parsed.toISOString().slice(0, 10);
};

const formatDateOnly = (value) => {
  const normalized = normalizeDateOnly(value);
  if (!normalized) return "N/A";

  const [year, month, day] = normalized.split("-");
  return `${month}/${day}/${year}`;
};

const AdminDashboard = () => {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(""); // page-level errors
  const [searchTerm, setSearchTerm] = useState("");
  const [filterTimeSlot, setFilterTimeSlot] = useState("");
  const [sortBy] = useState("createdAt");
  const [sortOrder] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [opened, setOpened] = useState(false);
  const [password, setPassword] = useState("");
  const correctPassword = "Geacom2025!";

  // Athena-related state
  const [athenaResults, setAthenaResults] = useState([]);
  const [createApptLoading, setCreateApptLoading] = useState(false);
  const [manualPatientIds, setManualPatientIds] = useState("");
  const [providerId, setProviderId] = useState("");
  const [appointmentTypeId, setAppointmentTypeId] = useState("");
  const [appointmentDate, setAppointmentDate] = useState("");
  const [appointmentTime, setAppointmentTime] = useState("");

  const [apptModalOpen, setApptModalOpen] = useState(false);
  const [modalTimeSlot, setModalTimeSlot] = useState("");
  const [profilesModalOpen, setProfilesModalOpen] = useState(false);
  const [globalError, setGlobalError] = useState(""); // modal-level errors
  const [createProfilesLoading, setCreateProfilesLoading] = useState(false);
  const [creatingAppts, setCreatingAppts] = useState(false);
  const [manualApptModalOpen, setManualApptModalOpen] = useState(false);
  const [cardDownloadKey, setCardDownloadKey] = useState("");

  // CHECK-IN MODAL STATE
  const [checkInModalOpen, setCheckInModalOpen] = useState(false);
  const [checkInIdsText, setCheckInIdsText] = useState("");
  const [checkInLoading, setCheckInLoading] = useState(false);

  const handleExportClick = () => setOpened(true);

  // Derived: patients in selected modal time slot
  const patientsForModalTimeSlot = useMemo(() => {
    if (!modalTimeSlot) return [];
    return patients.filter((p) => p.timeSlot === modalTimeSlot);
  }, [patients, modalTimeSlot]);

  // Fetch patients
  useEffect(() => {
    fetchPatients();
  }, []);

  const fetchPatients = async () => {
    try {
      setLoading(true);
      const response = await api.get("/patients");
      setPatients(response.data || []);
    } catch (err) {
      console.error("Fetch patients error:", err);
      const msg =
        err?.response?.data?.message ||
        err.message ||
        "Failed to load patient data";
      setError(msg);
      notifications.show({ title: "Error", message: msg, color: "red" });
    } finally {
      setLoading(false);
    }
  };

  const checkInAppointments = async (patientIds) => {
    try {
      const resp = await api.post("/athena/checkInAppointments", {
        patientIds,
      });

      notifications.show({
        title: "Athena Check-In",
        message: `Checked in ${resp.data?.checkedInCount || 0} patients`,
        color: "green",
      });

      return resp.data;
    } catch (err) {
      notifications.show({
        title: "Check-In Failed",
        message: err?.response?.data?.error || err.message,
        color: "red",
      });
    }
  };

  // Create Athena profiles for patients in the selected time slot
  const createAthenaProfiles = async () => {
    setGlobalError("");

    if (!modalTimeSlot) {
      const msg = "Please pick a time slot in the modal.";
      setGlobalError(msg);
      notifications.show({
        title: "Time slot required",
        message: msg,
        color: "red",
      });
      return;
    }

    const selected = patientsForModalTimeSlot;
    if (!selected.length) {
      const msg = "No patients found for the selected time slot.";
      setGlobalError(msg);
      notifications.show({ title: "No patients", message: msg, color: "red" });
      return;
    }

    setCreateProfilesLoading(true);

    try {
      const resp = await api.post("/athena/createPatients", {
        patients: selected,
      });
      const results = resp?.data?.results || [];
      setAthenaResults(results);
      setProfilesModalOpen(true);

      notifications.show({
        title: "Profiles Created",
        message: `${
          results.filter((r) => r.success).length
        } profiles created, ${
          results.filter((r) => !r.success).length
        } failed.`,
        color: "yellow",
      });
    } catch (err) {
      console.error("Create profiles error:", err);
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err.message ||
        "Failed to create Athena profiles";
      setGlobalError(msg);
      notifications.show({
        title: "Profile Creation Error",
        message: msg,
        color: "red",
        autoClose: 8000,
      });
    } finally {
      setCreateProfilesLoading(false);
    }
  };

  // Build payload and call backend for appointments
  const sendToBackend = async (data) => {
    setCreateApptLoading(true);
    try {
      const resp = await api.post("/athena/createAppointments", data);

      // If backend returns an error-style payload
      if (resp?.data?.error || resp?.data?.errors) {
        const msg = resp.data.error || JSON.stringify(resp.data.errors);
        notifications.show({
          title: "Appointment Error",
          message: msg,
          color: "red",
          autoClose: 8000,
        });
        return { success: false, data: resp.data };
      }

      notifications.show({
        title: "Appointments Created",
        message: "Appointments were created successfully.",
        color: "green",
      });
      console.log("Appointment result:", resp.data);
      return { success: true, data: resp.data };
    } catch (err) {
      console.error("Create appointments error:", err);
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err.message ||
        "Failed to create appointments";
      notifications.show({
        title: "Appointment Error",
        message: msg,
        color: "red",
        autoClose: 0,
      });
      return { success: false, error: msg };
    } finally {
      setCreateApptLoading(false);
    }
  };

  // Create appointments for patients that were successfully created in Athena
  const createAppointmentsForResults = async () => {
    setError("");
    setGlobalError("");

    if (!appointmentDate || !appointmentTime) {
      const msg = "Please provide both Appointment Date and Time.";
      setError(msg);
      notifications.show({
        title: "Missing Fields",
        message: msg,
        color: "red",
      });
      return;
    }

    if (!providerId || !appointmentTypeId) {
      const msg = "Provider ID and Appointment Type are required.";
      setError(msg);
      notifications.show({
        title: "Missing Fields",
        message: msg,
        color: "red",
      });
      return;
    }

    const patientIds = athenaResults
      .filter((r) => r.success)
      .map((r) => r.patientId)
      .filter(Boolean);

    if (patientIds.length === 0) {
      const msg = "No successful Athena patients to create appointments for.";
      setError(msg);
      notifications.show({ title: "No Patients", message: msg, color: "red" });
      return;
    }

    const payload = {
      providerId,
      appointmentTypeId,
      appointmentDate, // expected format MM/DD/YYYY or ISO depending on backend
      appointmentTime,
      appointments: patientIds.map((id) => ({ patientId: id })),
    };

    setCreatingAppts(true);
    const result = await sendToBackend(payload);
    setCreatingAppts(false);

    if (!result.success) {
      // show inline modal error too
      setGlobalError(
        result.error || "Failed to create appointments for results."
      );
    }
  };

  // Create appointments from manually-entered Athena IDs
  const createAppointmentsManual = async () => {
    setError("");
    setGlobalError("");

    if (!appointmentDate || !appointmentTime) {
      const msg = "Please provide both Appointment Date and Time.";
      setError(msg);
      notifications.show({
        title: "Missing Fields",
        message: msg,
        color: "red",
      });
      return;
    }

    if (!providerId || !appointmentTypeId) {
      const msg = "Provider ID and Appointment Type are required.";
      setError(msg);
      notifications.show({
        title: "Missing Fields",
        message: msg,
        color: "red",
      });
      return;
    }

    const ids = manualPatientIds
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean);

    if (ids.length === 0) {
      const msg = "Please enter at least one patient ID.";
      setError(msg);
      notifications.show({
        title: "No Patient IDs",
        message: msg,
        color: "red",
      });
      return;
    }

    const payload = {
      providerId,
      appointmentTypeId,
      appointmentDate,
      appointmentTime,
      appointments: ids.map((id) => ({ patientId: id })),
    };

    setCreatingAppts(true);
    const result = await sendToBackend(payload);
    setCreatingAppts(false);

    if (!result.success) {
      setGlobalError(result.error || "Failed to create manual appointments.");
    } else {
      // optionally clear manual IDs on success
      setManualPatientIds("");
    }
  };

  // Export CSV (protected by password modal)
  const handleConfirm = () => {
    if (password === correctPassword) {
      setOpened(false);
      setPassword("");
      exportData();
    } else {
      setError("Incorrect password");
      notifications.show({
        title: "Incorrect password",
        message: "The password you entered is incorrect.",
        color: "red",
      });
    }
  };

  // Helper: derived time slots
  const timeSlots = useMemo(
    () => [...new Set(patients.map((p) => p.timeSlot).filter(Boolean))],
    [patients]
  );

  const appointmentFormValid = () =>
    providerId.trim() &&
    appointmentTypeId.trim() &&
    appointmentDate.trim() &&
    appointmentTime.trim();

  const manualFormValid = () =>
    appointmentFormValid() &&
    manualPatientIds
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean).length > 0;

  // Filtering + sorting
  const filteredPatients = patients
    .filter((patient) => {
      const matchesSearch =
        searchTerm === "" ||
        patient.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        patient.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        patient.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        patient.organization?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesTimeSlot =
        filterTimeSlot === "" || patient.timeSlot === filterTimeSlot;

      return matchesSearch && matchesTimeSlot;
    })
    .sort((a, b) => {
      let valueA = a[sortBy];
      let valueB = b[sortBy];

      if (sortBy === "fullName") {
        valueA = `${a.firstName} ${a.lastName}`;
        valueB = `${b.firstName} ${b.lastName}`;
      }

      if (sortBy === "createdAt") {
        valueA = new Date(valueA);
        valueB = new Date(valueB);
      }

      if (sortBy === "dateOfBirth") {
        valueA = normalizeDateOnly(valueA);
        valueB = normalizeDateOnly(valueB);
      }

      if (typeof valueA === "string") {
        valueA = valueA.toLowerCase();
        valueB = valueB.toLowerCase();
      }

      return sortOrder === "asc"
        ? valueA < valueB
          ? -1
          : valueA > valueB
          ? 1
          : 0
        : valueA > valueB
        ? -1
        : valueA < valueB
        ? 1
        : 0;
    });

  const stats = {
    total: patients.length,
    timeSlots: [...new Set(patients.map((p) => p.timeSlot))].length,
    today: patients.filter(
      (p) => new Date(p.createdAt).toDateString() === new Date().toDateString()
    ).length,
    thisWeek: patients.filter((p) => {
      const now = new Date();
      const day = now.getDay(); // Sunday = 0, Monday = 1, ...
      const monday = new Date(now);
      const sunday = new Date(now);

      const diffToMonday = day === 0 ? -6 : 1 - day;
      monday.setDate(now.getDate() + diffToMonday);
      monday.setHours(0, 0, 0, 0);

      sunday.setDate(monday.getDate() + 6);
      sunday.setHours(23, 59, 59, 999);

      const created = new Date(p.createdAt);
      return created >= monday && created <= sunday;
    }).length,
  };

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const formatDateOfBirth = (dateString) => formatDateOnly(dateString);

  const downloadPatientFile = async ({
    url,
    downloadKey,
    fallbackFileName,
    errorMessage,
  }) => {
    try {
      setCardDownloadKey(downloadKey);
      const response = await api.get(url, {
        responseType: "blob",
      });
      const objectUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      const disposition = response.headers["content-disposition"] || "";
      const fileNameMatch = disposition.match(/filename="([^"]+)"/);

      link.href = objectUrl;
      link.download = fileNameMatch?.[1] || fallbackFileName;
      link.click();
      window.URL.revokeObjectURL(objectUrl);
    } catch (err) {
      let msg = errorMessage;

      if (typeof err?.response?.data?.text === "function") {
        try {
          const errorText = await err.response.data.text();
          const parsed = JSON.parse(errorText);
          msg = parsed?.message || msg;
        } catch (parseError) {
          msg = err?.message || msg;
        }
      } else {
        msg = err?.response?.data?.message || err?.message || msg;
      }

      notifications.show({
        title: "Download Error",
        message: msg,
        color: "red",
      });
    } finally {
      setCardDownloadKey("");
    }
  };

  const downloadInsuranceCard = (patientId, side, fallbackFileName) =>
    downloadPatientFile({
      url: `/patients/${patientId}/insurance-card/${side}`,
      downloadKey: `${patientId}-${side}`,
      fallbackFileName,
      errorMessage: "Failed to download insurance card image.",
    });

  const downloadConsentForm = (patientId, formType, fallbackFileName) =>
    downloadPatientFile({
      url: `/patients/${patientId}/consent-form/${formType}`,
      downloadKey: `${patientId}-${formType}`,
      fallbackFileName,
      errorMessage: "Failed to download signed consent form.",
    });

  // CSV Export with Patient Data full
  const exportData = () => {
    const csvContent = [
      [
        "Registration Date",
        "Time Slot",
        "First Name",
        "Middle Name",
        "Last Name",
        "Date of Birth",
        "SSN",
        "Sex",
        "Race",
        "Ethnicity",
        "Language",
        "Marital Status",
        "Street Address",
        "Street Address 2",
        "City",
        "State",
        "Zip Code",
        "Phone Number",
        "Email",
        "Organization",
        "Title/Role",
        "Insurance Provider",
        "Policy Holder First Name",
        "Policy Holder Last Name",
        "Policy Holder Address",
        "Policy Holder Sex",
        "Relationship to Policy Holder",
        "Policy Entity Type",
        "Policy Issue Date",
        "Policy Expiration Date",
        "Member ID",
        "Policy Group Number",
        "Insurance Card Front Uploaded",
        "Insurance Card Back Uploaded",
        "Insurance Card Retention Ends",
        "HIPAA Privacy Form Signed At",
        "Consent to Receive Care Signed At",
        "Submitted By",
      ].join(","),
      ...filteredPatients.map((patient) =>
        [
          formatDate(patient.createdAt),
          patient.timeSlot,
          patient.firstName,
          patient.middleName || "",
          patient.lastName,
          patient.dateOfBirth ? formatDateOnly(patient.dateOfBirth) : "",
          patient.ssn || "",
          patient.sex,
          patient.raceDeclined ? "Declined" : patient.race || "",
          patient.ethnicityDeclined ? "Declined" : patient.ethnicity || "",
          patient.language || "",
          patient.maritalStatus || "",
          patient.streetAddress,
          patient.streetAddress2 || "",
          patient.city,
          patient.state,
          patient.zipCode,
          patient.phoneNumber,
          patient.email,
          patient.organization || "",
          patient.titleRole || "",
          patient.insuranceName || "",
          patient.policyHolderFirstName || "",
          patient.policyHolderLastName || "",
          patient.policyHolderAddress || "",
          patient.policyHolderSex || "",
          patient.relationshipToPolicyHolder || "",
          patient.entityType || "",
          patient.issueDate ? formatDateOnly(patient.issueDate) : "",
          patient.expiration ? formatDateOnly(patient.expiration) : "",
          patient.memberID || "",
          patient.policyGroupNumber || "",
          patient.insuranceCardUploads?.front ? "Yes" : "No",
          patient.insuranceCardUploads?.back ? "Yes" : "No",
          patient.insuranceCardUploads?.front?.expiresAt
            ? formatDate(patient.insuranceCardUploads.front.expiresAt)
            : patient.insuranceCardUploads?.back?.expiresAt
            ? formatDate(patient.insuranceCardUploads.back.expiresAt)
            : "",
          patient.consentForms?.hipaaPrivacy?.signedAt
            ? formatDate(patient.consentForms.hipaaPrivacy.signedAt)
            : "",
          patient.consentForms?.consentToCare?.signedAt
            ? formatDate(patient.consentForms.consentToCare.signedAt)
            : "",
          patient.submittedBy,
        ]
          .map((field) => `"${String(field).replace(/\"/g, '""')}"`)
          .join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `patient_registrations_${
      new Date().toISOString().split("T")[0]
    }.csv`;
    link.click();
    window.URL.revokeObjectURL(url);

    notifications.show({
      title: "Exported",
      message: "CSV export started.",
      color: "blue",
    });
  };

  // CSV Export for Athena Results with Profiles
  const downloadCSV = () => {
    if (athenaResults.length === 0) return;

    const headers = ["First", "Last", "Athena ID", "Status"];

    const rows = athenaResults.map((r) => [
      r.firstName,
      r.lastName,
      r.patientId || "N/A",
      r.success ? "Created" : "Failed",
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((v) => `"${v}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "athena_profiles.csv");
    link.click();

    URL.revokeObjectURL(url);
  };

  // Pagination
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedPatients = filteredPatients.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  if (loading) {
    return (
      <Container size="lg" py="xl">
        <Group position="center" spacing="sm">
          <Loader size="lg" />
          <Text>Loading patient data...</Text>
        </Group>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack spacing="xl">
        <div>
          <Title order={2} align="center">
            Event Admin Dashboard
          </Title>
          <Text align="center" color="dimmed">
            Registration Overview
          </Text>
        </div>

        {error && (
          <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
            {error}
          </Alert>
        )}

        {/* Stats */}
        <Group grow>
          <Card
            shadow="sm"
            padding="lg"
            style={{ backgroundColor: "#6cbbf3ff" }}
          >
            <Title order={3} color="blue">
              {stats.total}
            </Title>
            <Text>Total Registrations</Text>
          </Card>

          <Card
            shadow="sm"
            padding="lg"
            style={{ backgroundColor: "#99e9f7ff" }}
          >
            <Title order={3} color="green">
              {stats.today}
            </Title>
            <Text>Registered Today</Text>
          </Card>

          <Card
            shadow="sm"
            padding="lg"
            style={{ backgroundColor: "#f3b0fdff" }}
          >
            <Title order={3} color="violet">
              {stats.thisWeek}
            </Title>
            <Text>This Week</Text>
          </Card>
        </Group>

        {/* Controls */}
        <Card withBorder shadow="sm" padding="lg">
          <Group align="flex-end" spacing="md">
            <TextInput
              label="Search Patients"
              placeholder="Name, Email, Insurance provider..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ flex: 1 }}
            />

            <Select
              label="Filter by Time Slot"
              placeholder="All Time Slots"
              value={filterTimeSlot}
              onChange={setFilterTimeSlot}
              data={[
                { value: "", label: "All Time Slots" },
                ...[...new Set(patients.map((p) => p.timeSlot))].map((t) => ({
                  value: t,
                  label: t,
                })),
              ]}
              style={{ flex: 1 }}
            />

            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-600">
                Showing {filteredPatients.length} of {patients.length}{" "}
                registrations
              </div>

              <Button
                onClick={handleExportClick}
                leftSection={<IconDownload size={18} />}
                variant="gradient"
                gradient={{ from: "blue", to: "purple" }}
                radius="md"
                size="sm"
              >
                Export to CSV
              </Button>
            </div>

            {/* Export modal */}
            <Modal
              opened={opened}
              onClose={() => {
                setOpened(false);
                setPassword("");
                setError("");
              }}
              title="Password required"
              centered
            >
              <div className="space-y-4">
                <Text size="sm">Enter the password to export data:</Text>
                <PasswordInput
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.currentTarget.value)}
                  icon={<IconLock size={16} />}
                  error={error}
                />
                <div className="flex justify-end gap-2">
                  <Button variant="light" onClick={() => setOpened(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleConfirm}>Confirm</Button>
                </div>
              </div>
            </Modal>

            {/* Athena modal */}
            <Modal
              opened={apptModalOpen}
              onClose={() => {
                setApptModalOpen(false);
                setModalTimeSlot("");
                setAthenaResults([]);
                setProfilesModalOpen(false);
                setGlobalError("");
              }}
              title="Create Athena Profiles & Appointments"
              size="lg"
              centered
            >
              <Stack spacing="md">
                <Text size="sm" color="dimmed">
                  Select a time slot and create Athena profiles for those
                  patients. After profiles are created, you can create
                  appointments for the successfully created patients in the same
                  modal.
                </Text>

                {!profilesModalOpen && (
                  <>
                    <Select
                      label="Time Slot (for creating profiles)"
                      placeholder="Select a time slot"
                      data={timeSlots.map((t) => ({ value: t, label: t }))}
                      value={modalTimeSlot}
                      onChange={(v) => setModalTimeSlot(v || "")}
                      error={!modalTimeSlot ? "Time slot required" : undefined}
                    />

                    <Group position="right" spacing="sm">
                      <Button
                        loading={createProfilesLoading}
                        disabled={!modalTimeSlot}
                        onClick={createAthenaProfiles}
                      >
                        Create Athena Profiles (Selected Time Slot)
                      </Button>
                    </Group>

                    <Divider />
                  </>
                )}

                {globalError && (
                  <Alert
                    icon={<IconAlertCircle size={16} />}
                    title="Error"
                    color="red"
                  >
                    {globalError}
                  </Alert>
                )}

                {/* show results if available */}
                {profilesModalOpen && (
                  <>
                    <Title order={5}>Created Profiles</Title>
                    <Group position="right" mb="xs">
                      <Button variant="light" onClick={downloadCSV}>
                        Download CSV
                      </Button>
                    </Group>

                    {athenaResults.length === 0 ? (
                      <Text c="dimmed">No results returned.</Text>
                    ) : (
                      <>
                        <ScrollArea style={{ maxHeight: 240 }}>
                          <Table striped highlightOnHover>
                            <Table.Thead>
                              <Table.Tr>
                                <Table.Th>First</Table.Th>
                                <Table.Th>Last</Table.Th>
                                <Table.Th>Athena ID</Table.Th>
                                <Table.Th>Status</Table.Th>
                              </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                              {athenaResults.map((r, i) => (
                                <Table.Tr key={i}>
                                  <Table.Td>{r.firstName}</Table.Td>
                                  <Table.Td>{r.lastName}</Table.Td>
                                  <Table.Td>{r.patientId || "N/A"}</Table.Td>
                                  <Table.Td>
                                    {r.success ? (
                                      <Text c="green">Created</Text>
                                    ) : (
                                      <Text c="red">Failed</Text>
                                    )}
                                  </Table.Td>
                                </Table.Tr>
                              ))}
                            </Table.Tbody>
                          </Table>
                        </ScrollArea>

                        <Divider />

                        {/* appointment form (shared) */}
                        <Stack spacing="sm">
                          <Select
                            label="Provider"
                            placeholder="Select provider"
                            data={PROVIDERS}
                            value={providerId}
                            onChange={(v) => setProviderId(v)}
                            error={!providerId ? "Required" : undefined}
                          />

                          <Select
                            label="Appointment Type"
                            placeholder="Select type"
                            value={appointmentTypeId}
                            onChange={setAppointmentTypeId}
                            error={!appointmentTypeId ? "Required" : undefined}
                            data={[
                              { value: "341", label: "Athlete AIRs" },
                              { value: "342", label: "Adult AIRs" },
                              { value: "322", label: "District AIRs" },
                            ]}
                          />

                          <Group>
                            <TextInput
                              label="Appointment Date"
                              placeholder="MM/DD/YYYY"
                              value={appointmentDate}
                              onChange={(e) =>
                                setAppointmentDate(e.currentTarget.value)
                              }
                              error={!appointmentDate ? "Required" : undefined}
                            />

                            <TextInput
                              label="Appointment Time"
                              placeholder="HH:mm"
                              value={appointmentTime}
                              onChange={(e) =>
                                setAppointmentTime(e.currentTarget.value)
                              }
                              error={!appointmentTime ? "Required" : undefined}
                            />
                          </Group>

                          <Group position="apart">
                            <Button
                              loading={creatingAppts}
                              disabled={
                                !appointmentFormValid() ||
                                athenaResults.length === 0 ||
                                athenaResults.some((r) => !r.success)
                              }
                              onClick={createAppointmentsForResults}
                            >
                              Create Appointments for Created Patients
                            </Button>
                          </Group>

                          {globalError && (
                            <Alert
                              color="red"
                              icon={<IconAlertCircle size={16} />}
                            >
                              {globalError}
                            </Alert>
                          )}
                        </Stack>
                      </>
                    )}
                  </>
                )}
              </Stack>
            </Modal>

            {/* Check In appointment modal */}
            <Modal
              opened={checkInModalOpen}
              onClose={() => setCheckInModalOpen(false)}
              title="Check In Patient's Appointment by Patient ID"
              size="md"
              centered
            >
              <Text size="sm" c="dimmed">
                Paste multiple patient IDs here (comma or newline separated):
              </Text>
              <Alert mb="md" color="red" icon={<IconAlertCircle size={16} />}>
                All the appointments associated with the provided patient IDs
                will be checked in.
              </Alert>

              <Textarea
                placeholder="12345, 67890, 112233"
                minRows={6}
                value={checkInIdsText}
                onChange={(e) => setCheckInIdsText(e.currentTarget.value)}
              />

              <Group justify="right" mt="md">
                <Button
                  variant="default"
                  onClick={() => setCheckInModalOpen(false)}
                >
                  Cancel
                </Button>

                <Button
                  loading={checkInLoading}
                  onClick={async () => {
                    setCheckInLoading(true);

                    // Convert input to array of clean numbers
                    const ids = checkInIdsText
                      .split(/[\n, ]+/) // split by comma, space, newline
                      .filter(Boolean) // remove empty
                      .map((id) => id.trim());

                    await checkInAppointments(ids);

                    setCheckInLoading(false);
                  }}
                >
                  Check In
                </Button>
              </Group>
            </Modal>

            {/* Manual appointment modal */}
            <Modal
              opened={manualApptModalOpen}
              onClose={() => {
                setManualApptModalOpen(false);
                setManualPatientIds("");
                setGlobalError("");
              }}
              title="Create Manual Appointments"
              centered
            >
              <Stack spacing="sm">
                <Text size="sm" c="dimmed">
                  Enter patient Athena IDs (comma separated) and the shared
                  appointment details.
                </Text>

                <TextInput
                  label="Patient IDs (comma separated)"
                  placeholder="1234, 5678"
                  value={manualPatientIds}
                  onChange={(e) => setManualPatientIds(e.currentTarget.value)}
                  error={
                    !manualPatientIds
                      ? "Enter at least one patient ID"
                      : undefined
                  }
                />

                <Select
                  label="Provider"
                  placeholder="Select provider"
                  data={PROVIDERS}
                  value={providerId}
                  onChange={(v) => setProviderId(v)}
                  error={!providerId ? "Required" : undefined}
                />

                <Select
                  label="Appointment Type"
                  placeholder="Select appointment type"
                  data={[
                    { value: "341", label: "Athlete AIRs" },
                    { value: "342", label: "Adult AIRs" },
                    { value: "322", label: "District AIRs" },
                  ]}
                  value={appointmentTypeId}
                  onChange={(val) => setAppointmentTypeId(val || "")}
                  error={!appointmentTypeId ? "Required" : undefined}
                />

                <Group>
                  <div className="date-input" style={{ flex: 1 }}>
                    <label
                      style={{
                        fontSize: 14,
                        fontWeight: 500,
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Appointment Date
                    </label>

                    <input
                      type="date"
                      value={appointmentDate}
                      onChange={(e) => setAppointmentDate(e.target.value)}
                      style={{
                        padding: "8px 12px",
                        borderRadius: 6,
                        border: "1px solid #ced4da",
                        width: "100%",
                        fontSize: 14,
                      }}
                    />

                    {!appointmentDate && (
                      <p style={{ color: "red", fontSize: 12, marginTop: 4 }}>
                        Required
                      </p>
                    )}
                  </div>

                  <TextInput
                    label="Appointment Time"
                    placeholder="HH:mm"
                    value={appointmentTime}
                    onChange={(e) => setAppointmentTime(e.currentTarget.value)}
                    error={!appointmentTime ? "Required" : undefined}
                  />
                </Group>

                <Group position="right" spacing="sm">
                  <Button
                    loading={creatingAppts}
                    disabled={!manualFormValid()}
                    onClick={createAppointmentsManual}
                  >
                    Create Appointments
                  </Button>
                </Group>

                {globalError && (
                  <Alert color="red" icon={<IconAlertCircle size={16} />}>
                    {globalError}
                  </Alert>
                )}
              </Stack>
            </Modal>
          </Group>

          <Divider my="md" />

          <Text size="sm" c="dimmed">
            Showing {filteredPatients.length} of {patients.length} registrations
          </Text>

          <Group justify="flex-end">
            <Button
              onClick={() => {
                setModalTimeSlot("");
                setProfilesModalOpen(false);
                setApptModalOpen(true);
              }}
              variant="outline"
              radius="md"
            >
              Create Athena Profiles & Appointments
            </Button>
            <Button
              onClick={() => setManualApptModalOpen(true)}
              variant="light"
              radius="md"
            >
              Create Athena Appointments for IDs
            </Button>

            <Button color="green" onClick={() => setCheckInModalOpen(true)}>
              Manual Check-In
            </Button>
          </Group>
        </Card>

        {/* Patient Table */}
        <Card withBorder shadow="sm" padding="lg">
          <Title order={4} mb="md">
            All Event Registrations
          </Title>

          {filteredPatients.length === 0 ? (
            <Text c="dimmed" align="center" py="xl">
              {patients.length === 0
                ? "No registrations yet."
                : "No registrations match your filters."}
            </Text>
          ) : (
            <>
              <ScrollArea>
                <Table striped highlightOnHover withColumnBorders>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Time Slot</Table.Th>
                      <Table.Th>Registered At</Table.Th>
                      <Table.Th>Name</Table.Th>
                      <Table.Th>DOB</Table.Th>
                      <Table.Th>Sex</Table.Th>
                      <Table.Th>Email</Table.Th>
                      <Table.Th>Phone</Table.Th>
                      <Table.Th>Insurance Provider</Table.Th>
                      <Table.Th>Insurance Card</Table.Th>
                      <Table.Th>Consent Forms</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {paginatedPatients.map((p) => (
                      <Table.Tr key={p._id}>
                        <Table.Td>{p.timeSlot}</Table.Td>
                        <Table.Td>{formatDate(p.createdAt)}</Table.Td>
                        <Table.Td>
                          {p.firstName} {p.middleName || ""} {p.lastName}
                        </Table.Td>
                        <Table.Td>
                          {p.dateOfBirth
                            ? formatDateOfBirth(p.dateOfBirth)
                            : "N/A"}
                        </Table.Td>
                        <Table.Td>{p.sex || "N/A"}</Table.Td>
                        <Table.Td>{p.email}</Table.Td>
                        <Table.Td>{p.phoneNumber}</Table.Td>
                        <Table.Td>{p.insuranceName || "N/A"}</Table.Td>
                        <Table.Td>
                          <Group gap="xs" wrap="wrap">
                            <Button
                              size="xs"
                              variant="light"
                              disabled={!p.insuranceCardUploads?.front}
                              loading={cardDownloadKey === `${p._id}-front`}
                              onClick={() =>
                                downloadInsuranceCard(
                                  p._id,
                                  "front",
                                  `${p.lastName || "patient"}_${p.firstName || "card"}_front`
                                )
                              }
                            >
                              Front
                            </Button>
                            <Button
                              size="xs"
                              variant="light"
                              disabled={!p.insuranceCardUploads?.back}
                              loading={cardDownloadKey === `${p._id}-back`}
                              onClick={() =>
                                downloadInsuranceCard(
                                  p._id,
                                  "back",
                                  `${p.lastName || "patient"}_${p.firstName || "card"}_back`
                                )
                              }
                            >
                              Back
                            </Button>
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          <Group gap="xs" wrap="wrap">
                            <Button
                              size="xs"
                              variant="light"
                              disabled={!p.consentForms?.hipaaPrivacy?.signedAt}
                              loading={
                                cardDownloadKey === `${p._id}-hipaaPrivacy`
                              }
                              onClick={() =>
                                downloadConsentForm(
                                  p._id,
                                  "hipaaPrivacy",
                                  `${p.lastName || "patient"}_${p.firstName || "consent"}_hipaa.pdf`
                                )
                              }
                            >
                              HIPAA
                            </Button>
                            <Button
                              size="xs"
                              variant="light"
                              disabled={!p.consentForms?.consentToCare?.signedAt}
                              loading={
                                cardDownloadKey === `${p._id}-consentToCare`
                              }
                              onClick={() =>
                                downloadConsentForm(
                                  p._id,
                                  "consentToCare",
                                  `${p.lastName || "patient"}_${p.firstName || "consent"}_consent_to_care.pdf`
                                )
                              }
                            >
                              Care Consent
                            </Button>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </ScrollArea>

              <Group position="center" mt="md">
                <Pagination
                  total={Math.ceil(filteredPatients.length / itemsPerPage)}
                  page={currentPage}
                  onChange={setCurrentPage}
                />
              </Group>
            </>
          )}
        </Card>
      </Stack>
    </Container>
  );
};

export default AdminDashboard;
